import { useCallback, useMemo } from 'react';
import { Box, Text, PressableIcon, BoxProps } from '@bonanza-social/moonlight';
import { StackNavigationProp, useNavigation } from '@bonanza-social/navigation';
import RegistrationOfferImage from '../../RegistrationOfferImage/RegistrationOfferImage';
import { RewardCardType } from './RewardCard.types';
import { Promotion } from '../../../services/RegistrationOffersService/RegistrationOffersService.schemas';
import { formatCompact } from '@bonanza-social/toolkit/utils';
import { rewardBadgeDataMap } from '../../RewardBadge/RewardBadge.constants';
import { PromotionT } from '../HeaderVariant';

type Props = {
  type: RewardCardType;
  promotion: Promotion;
  referralPromo?: PromotionT | null;
  containerProps?: BoxProps;
};

function RewardCardHorizontal({
  type,
  promotion,
  referralPromo,
  containerProps,
}: Props) {
  const navigation =
    useNavigation<StackNavigationProp<AuthNavigatorParamsList>>();
  const imageSize = {
    mobile: referralPromo ? 42 : 60,
    tablet: 60,
    desktop: 60,
  };

  const handleInfoPress = useCallback(
    () => navigation.navigate('RAFTerms'),
    [navigation],
  );

  const [rewardAmount, rewardTarget] = useMemo(() => {
    const r = promotion.rewards?.[0];
    return [r?.rewardAmount ?? 0, r?.rewardTarget ?? ''];
  }, [promotion]);

  const rewardLabel =
    rewardBadgeDataMap[rewardTarget]?.rewardName ?? rewardTarget;

  const subtitle =
    type === RewardCardType.WELCOME ? 'Welcome Bonus' : 'Invitation Bonus';

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      borderRadius="radius-200"
      {...containerProps}
    >
      <RegistrationOfferImage
        width={imageSize}
        height={imageSize}
        marginRight="space-200"
        rewards={promotion.rewards}
        miniImageUrl={promotion?.miniImageUrl || ''}
      />

      <Box flex={1} flexDirection="row" alignItems="center">
        <Box flex={1} flexDirection="row">
          <Box paddingVertical="space-300">
            <Box flexDirection="row" gap="space-100" alignItems="center">
              <Text variant="body-s-semi" color="darkSurfacePrimary">
                {formatCompact(rewardAmount)}
              </Text>
              <Text
                numberOfLines={1}
                maxWidth={{ mobile: 70, tablet: '100%', desktop: '100%' }}
                variant="body-s"
                color="darkSurfacePrimary"
              >
                {rewardLabel}
              </Text>
              {type === RewardCardType.REFERRER && (
                <PressableIcon
                  marginRight="space-200"
                  name="info"
                  color="iconSecondary"
                  width={16}
                  height={16}
                  onPress={handleInfoPress}
                />
              )}
            </Box>

            <Box>
              <Text variant="body-s" color="darkSurfaceSecondary">
                {subtitle}
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default RewardCardHorizontal;
